import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import type { ChangeEvent } from "react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useBreadcrumbs } from "../../contexts/BreadcrumbContext";
import { useSnackbar } from "../../contexts/SnackbarContext";
import {
  getAdminProduct,
  updateAdminProduct,
} from "../../services/order.service";
import { ProductType, ProductUpdateRequest } from "../../types/order";

type FormState = {
  code: string;
  description: string;
  custom_description: string;
  search_string: string;
  fclo: string;
  design_code: string;
  chromatic_code: string;
  material_code: string;
  index_code: string;
  cylinder_min: string;
  cylinder_max: string;
  sphere_min: string;
  sphere_max: string;
  additional_min: string;
  additional_max: string;
  product_type: ProductType;
};

const initialForm: FormState = {
  code: "",
  description: "",
  custom_description: "",
  search_string: "",
  fclo: "",
  design_code: "",
  chromatic_code: "",
  material_code: "",
  index_code: "",
  cylinder_min: "",
  cylinder_max: "",
  sphere_min: "",
  sphere_max: "",
  additional_min: "",
  additional_max: "",
  product_type: "RX",
};

type FieldDef = {
  key: keyof FormState;
  label: string;
  placeholder: string;
  required?: boolean;
};

const rows: Array<[FieldDef, FieldDef?]> = [
  [
    { key: "code", label: "Code", placeholder: "Enter Code", required: true },
    {
      key: "description",
      label: "Description",
      placeholder: "Enter Description",
      required: true,
    },
  ],
  [
    {
      key: "custom_description",
      label: "Custom Description",
      placeholder: "Enter Custom Description",
      required: true,
    },
    {
      key: "search_string",
      label: "Searchstring",
      placeholder: "Enter Searchstring",
      required: true,
    },
  ],
  [
    { key: "fclo", label: "Fclo", placeholder: "Enter Fclo", required: true },
    {
      key: "design_code",
      label: "Design Code",
      placeholder: "Enter Design Code",
      required: true,
    },
  ],
  [
    {
      key: "chromatic_code",
      label: "Chromatic Code",
      placeholder: "Enter Chromatic Code",
      required: true,
    },
    {
      key: "material_code",
      label: "Material Code",
      placeholder: "Enter Material Code",
      required: true,
    },
  ],
  [
    {
      key: "index_code",
      label: "Index Code",
      placeholder: "Enter Index Code",
      required: true,
    },
    {
      key: "cylinder_min",
      label: "Cylinder Min",
      placeholder: "Enter Cylinder Min",
      required: true,
    },
  ],
  [
    {
      key: "cylinder_max",
      label: "Cylinder Max",
      placeholder: "Enter Cylinder Max",
      required: true,
    },
    {
      key: "sphere_min",
      label: "Sphere Min",
      placeholder: "Enter Sphere Min",
      required: true,
    },
  ],
  [
    {
      key: "sphere_max",
      label: "Sphere Max",
      placeholder: "Enter Sphere Max",
      required: true,
    },
    {
      key: "additional_min",
      label: "Addition Min",
      placeholder: "Enter Addition Min",
      required: true,
    },
  ],
  [
    {
      key: "additional_max",
      label: "Addition Max",
      placeholder: "Enter Addition Max",
      required: true,
    },
  ],
];

const requiredFields: Array<keyof FormState> = [
  "code",
  "description",
  "custom_description",
  "search_string",
  "fclo",
  "design_code",
  "chromatic_code",
  "material_code",
  "index_code",
  "cylinder_min",
  "cylinder_max",
  "sphere_min",
  "sphere_max",
  "additional_min",
  "additional_max",
];

export default function CrmProductMasterEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { setBreadcrumbs } = useBreadcrumbs();
  const { showSnackbar } = useSnackbar();
  const [form, setForm] = useState<FormState>(initialForm);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setBreadcrumbs([
      { label: "Product Master", to: "/crm/product-master" },
      { label: "Edit Product Master", to: `/crm/product-master/${id}/edit` },
    ]);
  }, [id, setBreadcrumbs]);

  useEffect(() => {
    const load = async () => {
      if (!id) {
        navigate("/crm/product-master");
        return;
      }

      setIsLoading(true);
      try {
        const product = await getAdminProduct(id);
        setForm({
          code: product.code || "",
          description: product.description || "",
          custom_description: product.custom_description || "",
          search_string: product.search_string || "",
          fclo: product.fclo || "",
          design_code: product.design_code || "",
          chromatic_code: product.chromatic_code || "",
          material_code: product.material_code || "",
          index_code: product.index_code || "",
          cylinder_min: product.cylinder_min || "",
          cylinder_max: product.cylinder_max || "",
          sphere_min: product.sphere_min || "",
          sphere_max: product.sphere_max || "",
          additional_min: product.additional_min || "",
          additional_max: product.additional_max || "",
          product_type: product.product_type || "RX",
        });
      } catch (error) {
        console.error("Error loading product:", error);
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [id, navigate]);

  const errors = useMemo(() => {
    if (!submitted) return new Set<string>();

    return new Set(
      requiredFields
        .filter((key) => !String(form[key] || "").trim())
        .map(String),
    );
  }, [form, submitted]);

  const onFieldChange =
    (key: keyof FormState) => (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setForm((prev) => ({ ...prev, [key]: value }));
    };

  const handleSubmit = async () => {
    if (!id) return;

    setSubmitted(true);
    const hasMissingRequired = requiredFields.some(
      (key) => !String(form[key] || "").trim(),
    );
    if (hasMissingRequired) return;

    setIsSubmitting(true);
    try {
      const payload: ProductUpdateRequest = {
        product_type: form.product_type,
        description: form.description,
        custom_description: form.custom_description,
        search_string: form.search_string,
        fclo: form.fclo,
        design_code: form.design_code,
        chromatic_code: form.chromatic_code,
        material_code: form.material_code,
        index_code: form.index_code,
        cylinder_min: form.cylinder_min,
        cylinder_max: form.cylinder_max,
        sphere_min: form.sphere_min,
        sphere_max: form.sphere_max,
        additional_min: form.additional_min,
        additional_max: form.additional_max,
      };

      await updateAdminProduct(id, payload);
      showSnackbar("Product updated successfully", "success");
      navigate("/crm/product-master");
    } catch (error) {
      console.error("Error updating product:", error);
      showSnackbar("Failed to update product", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{ minHeight: 320 }}
      >
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Stack spacing={3}>
      <Box>
        <Paper sx={{ borderRadius: 1, overflow: "hidden" }}>
          <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #D8DBDF" }}>
            <Typography sx={{ fontSize: 20, fontWeight: 600, lineHeight: 1.4 }}>
              Product Master Info
            </Typography>
          </Box>

          <Stack spacing={2} sx={{ px: 2, pt: 2 }}>
            {rows.map(([left, right]) => (
              <Stack
                key={left.key}
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                alignItems="flex-start"
              >
                <Box sx={{ flex: 1, width: "100%" }}>
                  <Typography sx={{ fontSize: 16, color: "#111", mb: 0.75 }}>
                    {left.label}
                    {left.required && (
                      <Box component="span" sx={{ color: "#BE2217" }}>
                        {" "}
                        *
                      </Box>
                    )}
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={left.placeholder}
                    value={String(form[left.key] || "")}
                    onChange={onFieldChange(left.key)}
                    error={errors.has(String(left.key))}
                    helperText={
                      errors.has(String(left.key))
                        ? `${left.label} is required`
                        : " "
                    }
                    disabled={left.key === "code"}
                  />
                </Box>

                {right ? (
                  <Box sx={{ flex: 1, width: "100%" }}>
                    <Typography sx={{ fontSize: 16, color: "#111", mb: 0.75 }}>
                      {right.label}
                      {right.required && (
                        <Box component="span" sx={{ color: "#BE2217" }}>
                          {" "}
                          *
                        </Box>
                      )}
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder={right.placeholder}
                      value={String(form[right.key] || "")}
                      onChange={onFieldChange(right.key)}
                      error={errors.has(String(right.key))}
                      helperText={
                        errors.has(String(right.key))
                          ? `${right.label} is required`
                          : " "
                      }
                    />
                  </Box>
                ) : (
                  <Box sx={{ flex: 1, width: "100%" }} />
                )}
              </Stack>
            ))}
          </Stack>

          <Stack
            direction="row"
            justifyContent="flex-end"
            spacing={2}
            sx={{ px: 2, py: 2, mt: 2 }}
          >
            <Button
              variant="outlined"
              onClick={() => navigate("/crm/product-master")}
              sx={{
                borderRadius: "96px",
                borderColor: "#111",
                color: "#111",
                px: 3,
                textTransform: "none",
              }}
              disabled={isSubmitting}
            >
              Discard
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              sx={{
                borderRadius: "96px",
                bgcolor: "#111",
                px: 3,
                textTransform: "none",
                boxShadow: "none",
                "&:hover": { bgcolor: "#000", boxShadow: "none" },
              }}
              disabled={isSubmitting}
            >
              Save
            </Button>
          </Stack>
        </Paper>
      </Box>
    </Stack>
  );
}
